import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OGTags } from "@/components/OGTags";
import { ScrollReveal, ScrollRevealItem } from "@/components/ScrollReveal";
import { getShopifyImageUrl, fetchCollections } from "@/lib/shopify";
import { getCategoryBySlug, getDescendantCategories } from "@/lib/categories";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { isCategoryKnownEmpty, useCategoryStockVersion } from "@/lib/categoryStock";

import collectionLiving from "@/assets/collection-living.webp";
import collectionSofas from "@/assets/collection-sofas.webp";
import collectionRugs from "@/assets/collection-rugs.webp";
import collectionDiningTables from "@/assets/collection-dining-tables.webp";
import collectionChandeliers from "@/assets/collection-chandeliers.webp";
import collectionDecor from "@/assets/collection-decor.webp";
import collectionOutdoor from "@/assets/collection-outdoor.webp";
import collectionBedroom from "@/assets/collection-bedroom.webp";
import collectionBathroom from "@/assets/collection-bathroom.webp";
import categoriesHero from "@/assets/categories-hero.jpg";
import { FREE_SHIPPING_THRESHOLD } from "@/lib/shipping";

const fallbackImages: Record<string, string> = {
  living: collectionLiving,
  "living-room": collectionLiving,
  sofas: collectionSofas,
  "area-rugs": collectionRugs,
  rugs: collectionRugs,
  dining: collectionDiningTables,
  "dining-tables": collectionDiningTables,
  chandeliers: collectionChandeliers,
  lighting: collectionChandeliers,
  decor: collectionDecor,
  outdoor: collectionOutdoor,
  bedroom: collectionBedroom,
  "bed-bath": collectionBedroom,
  bathroom: collectionBathroom,
};

// Top-level categories (no parent, exclude curated/special)
const topLevelSlugs = ["living", "bed-bath", "bathroom", "dining", "office", "outdoor", "lighting", "decor", "area-rugs"];

const staticCategories = [
  { name: "Living Room", image: collectionLiving, slug: "living" },
  { name: "Bedroom", image: collectionBedroom, slug: "bed-bath" },
  { name: "Bathroom", image: fallbackImages.bathroom, slug: "bathroom" },
  { name: "Dining Room", image: collectionDiningTables, slug: "dining" },
  { name: "Office", image: collectionSofas, slug: "office" },
  { name: "Outdoor", image: collectionOutdoor, slug: "outdoor" },
  { name: "Lighting", image: collectionChandeliers, slug: "lighting" },
  { name: "Accents & Decor", image: collectionDecor, slug: "decor" },
  { name: "Area Rugs", image: collectionRugs, slug: "area-rugs" },
];

interface CategoryItem {
  name: string;
  image: string;
  slug: string;
}

const CategoryCard = ({ cat, index }: { cat: CategoryItem; index: number }) => {
  const [expanded, setExpanded] = useState(true);
  const subs = getDescendantCategories(cat.slug).filter(
    (sub) => !isCategoryKnownEmpty(sub.slug),
  );

  return (
    <ScrollRevealItem>
      <div className="overflow-hidden">
        <Link
          to={`/category/${cat.slug}`}
          className="group block"
        >
          <div className="aspect-[3/4] relative overflow-hidden bg-muted">
            <img
              src={
                cat.image.includes("cdn.shopify.com")
                  ? getShopifyImageUrl(cat.image, 500)
                  : cat.image
              }
              srcSet={
                cat.image.includes("cdn.shopify.com")
                  ? `${getShopifyImageUrl(cat.image, 300)} 300w, ${getShopifyImageUrl(cat.image, 500)} 500w, ${getShopifyImageUrl(cat.image, 800)} 800w`
                  : undefined
              }
              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              alt={cat.name}
              loading={index < 4 ? "eager" : "lazy"}
              className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/25 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-primary-foreground">
                {cat.name}
              </h2>
              <span className="inline-flex items-center gap-1.5 text-xs text-primary-foreground/80 mt-1.5 group-hover:text-primary-foreground transition-colors">
                Shop Now
                <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </div>
          </div>
        </Link>

        {/* Subcategories toggle */}
        {subs.length > 0 && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors text-sm font-medium text-foreground"
            >
              <span>{subs.length} Subcategories</span>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-300 ${
                  expanded ? "rotate-180" : ""
                }`}
              />
            </button>
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden bg-muted/30"
                >
                  <div className="px-4 py-2 space-y-0.5">
                    {subs.map((sub) => (
                      <Link
                        key={sub.slug}
                        to={`/category/${sub.slug}`}
                        className="flex items-center justify-between py-2 px-2 rounded-md text-sm text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors group/sub"
                      >
                        <span>{sub.name}</span>
                        <ArrowRight className="h-3 w-3 opacity-0 group-hover/sub:opacity-100 group-hover/sub:translate-x-0.5 transition-all duration-200 text-primary" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </ScrollRevealItem>
  );
};

const Categories = () => {
  const [categories, setCategories] = useState<CategoryItem[]>(staticCategories);
  // Categories measured to have zero in-stock products are not browsable
  // destinations — their pages still work, they just aren't advertised here.
  useCategoryStockVersion();
  const visibleCategories = categories.filter((cat) => !isCategoryKnownEmpty(cat.slug));

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCollections(30);
        if (data.length > 0) {
          const mapped: CategoryItem[] = topLevelSlugs
            .map((slug) => {
              const shopifyMatch = data.find((c) => c.node.handle === slug);
              const catDef = getCategoryBySlug(slug);
              if (!catDef) return null;
              return {
                name: catDef.name,
                image: shopifyMatch?.node.image?.url || fallbackImages[slug] || collectionLiving,
                slug,
              };
            })
            .filter(Boolean) as CategoryItem[];
          if (mapped.length > 0) setCategories(mapped);
        }
      } catch {
        // keep static
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <OGTags
        title="Furniture Categories — Shop by Room | LoftLoom"
        description={`Browse modern furniture by room: living, bedroom, dining, office, outdoor, lighting, decor and rugs. Free U.S. shipping over $${FREE_SHIPPING_THRESHOLD} and 30-day returns.`}
        url="https://loftloom.com/categories"
        canonical="https://loftloom.com/categories"
      />
      <Header />
      <Breadcrumbs items={[{ label: "All Categories" }]} />

      {/* Hero with background image */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={categoriesHero}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground/60" />
        </div>
        <div className="relative container text-center py-16 md:py-24">
          <p className="text-[11px] tracking-[0.3em] uppercase text-primary-foreground/70 font-semibold mb-2">
            Explore
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-normal text-primary-foreground mb-3">
            Shop by Category
          </h1>
          <p className="text-primary-foreground/70 text-sm md:text-base max-w-xl mx-auto">
            Browse our full collection of curated furniture and décor, organized by room and style.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 md:py-16">
        <div className="container">
          <ScrollReveal stagger staggerDelay={0.06}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {visibleCategories.map((cat, i) => (
                <CategoryCard key={cat.slug} cat={cat} index={i} />
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Get the Look banner */}
      <section className="container pb-12 md:pb-16">
        <Link to="/get-the-look" className="group relative block overflow-hidden">
          <img
            src="/journal/28-hero.webp"
            alt="Warm minimalist living room scene"
            loading="lazy"
            className="w-full h-[220px] md:h-[320px] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-foreground/30 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-primary-foreground/70 font-semibold mb-2">
              Shoppable Scenes
            </p>
            <h2 className="font-display text-2xl md:text-4xl text-primary-foreground tracking-[0.02em]">
              Get the Look
            </h2>
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-primary-foreground mt-3 group-hover:text-primary-foreground/80 transition-colors">
              Explore the rooms
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </div>
        </Link>

        <Link
          to="/style-quiz"
          className="group mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border border-border bg-card px-5 py-5 md:px-8 md:py-6 hover:border-primary transition-colors"
          aria-label="Take the style quiz to build a personalised room board"
        >
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-semibold mb-1.5">
              One Minute, Five Questions
            </p>
            <h2 className="font-display text-xl md:text-2xl text-foreground tracking-[0.02em]">
              Style Quiz
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
              Answer five questions and we'll build a shoppable room board for you.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-foreground group-hover:text-primary transition-colors shrink-0">
            Start the quiz
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        </Link>
        <div className="mt-10">
          <h2 className="font-display text-xl md:text-2xl text-foreground tracking-[0.02em] mb-4">Shop by Style</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { slug: "modern-minimalist", name: "Modern Minimalist", note: "Black, white, clean lines" },
              { slug: "warm-earthy", name: "Warm Earthy", note: "Wood, linen, rattan, jute" },
              { slug: "architectural", name: "Architectural", note: "Arches, geometry, marble" },
            ].map((s) => (
              <Link key={s.slug} to={`/category/${s.slug}`} className="group border border-border bg-card px-5 py-4 hover:border-primary transition-colors">
                <span className="block font-medium text-foreground group-hover:text-primary">{s.name}</span>
                <span className="block text-sm text-muted-foreground mt-1">{s.note}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default Categories;
